import { useLocation } from "wouter"
import { type Session } from "shared"
import { format, toZonedTime } from "date-fns-tz"
import cx from "clsx"
import ClockIcon from "../../../assets/clock.svg?react"
import { Avatar } from "@rawa/ui/Avatar"
import { ListingCard, ListingCardSection } from "../components/ListingCard"
import { KioskListLayout } from "../components/KioskListLayout"
import { fetchSessions } from "../api"
import { paths, pathHelpers } from "../../../paths"
import { useKioskIdleTimeout } from "../hooks/useKioskIdleTimeout"
import { useKioskQuery } from "../hooks/useKioskQuery"
import { useCreateInvitation } from "../hooks/useCreateInvitation"
import typography from "@rawa/ui/styles/typography.module.css"
import styles from "./SessionListPage.module.scss"

const TZ = "Australia/Perth"

function formatSessionTime(scheduledStart: string): string {
  return format(toZonedTime(new Date(scheduledStart), TZ), "h:mm aaa", {
    timeZone: TZ,
  })
}

type Props = {
  onInvitationCreated: (invitationId: string) => void
}

export function SessionListPage({ onInvitationCreated }: Props) {
  const [, navigate] = useLocation()
  useKioskIdleTimeout()

  // Failures are reported by the query client under this source.
  const { data, loading, failed } = useKioskQuery({
    queryKey: ["kiosk-sessions"],
    queryFn: fetchSessions,
    meta: { source: "fetchSessions" },
  })
  const sessions = data ?? []
  const loadError = failed ? "Failed to load sessions. Please try again." : null

  const { create, error: invitationError } =
    useCreateInvitation(onInvitationCreated)
  const error = loadError ?? invitationError

  async function handleSessionTap(session: Session) {
    if (session.contacts.length > 1) {
      navigate(pathHelpers.toKioskSession(session.id))
      return
    }
    await create(session.id, session.contacts[0].id)
  }

  return (
    <KioskListLayout
      heading="Select your appointment below"
      back={{ href: paths.kioskWelcome, label: "Back to home screen" }}
    >
      {loading && (
        <div
          role="status"
          aria-label="Loading sessions"
          className={styles.skeletonList}
        >
          {[0, 1, 2].map((i) => (
            <span key={i} className={styles.skeletonRow} />
          ))}
        </div>
      )}
      {!loading && !error && sessions.length === 0 && (
        <div className={styles.emptyState}>
          <ClockIcon width="48" height="48" aria-hidden="true" />
          <p className={cx(typography.bodyLarge, styles.emptyHeading)}>
            No sessions right now
          </p>
          <p className={cx(typography.bodyMedium, styles.emptyBody)}>
            Please speak to a staff member for assistance.
          </p>
        </div>
      )}
      <ul className={styles.sessionList}>
        {sessions.map((session) => (
          <li key={session.id}>
            <ListingCard onClick={() => handleSessionTap(session)}>
              <ListingCardSection tag="Session">
                <ClockIcon
                  width="24"
                  height="24"
                  className={styles.clockIcon}
                  aria-hidden="true"
                />
                <span>{formatSessionTime(session.scheduledStart)}</span>
              </ListingCardSection>
              {/* Fixed so the practitioner column does not resize row to row as
                  names vary in length. */}
              <ListingCardSection tag="Practitioner" width="180px">
                <Avatar name={session.practitionerFirstName} />
                <span>{session.practitionerFirstName}</span>
              </ListingCardSection>
            </ListingCard>
          </li>
        ))}
      </ul>
      {error && (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      )}
    </KioskListLayout>
  )
}
